import React, { useRef, useEffect, useState } from "react";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { BufferGeometryUtils } from "three/examples/jsm/utils/BufferGeometryUtils";
import * as THREE from "three";
import resizeImg from "../../utils/resizeImg";
import toBase64 from "../../utils/toBase64";
import Loader from "./Loader";
import { FRONT_SIDE, BACK_SIDE, LEFT_SIDE, RIGHT_SIDE } from "./models";
import Watermark from "./Watermark";

// globals
let scene,
  camera,
  renderer,
  controls,
  selectedModel,
  texture,
  textureTransparent,
  altTexture,
  logoTexture,
  logosPlane,
  wrapPlane,
  envMap,
  modelGroup,
  isMounted;

let meshLogoClones = [];
let meshWrapClones = [];
const strMime = "image/png";
const textureLoader = new THREE.TextureLoader();

const SceneComponent = ({
  model,
  selectedSwatches,
  selectedFocusCoordinates,
  selectedLogoImages,
  wrapImage,
  availableSides,
  summaryViewsTimestamp,
  onSummaryViewsGenerated,
  onModelLoaded,
  onFocus,
}) => {
  const container = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedFocusCoordinates && isMounted) {
      focusModel(selectedFocusCoordinates);
    }
  }, [selectedFocusCoordinates]);

  useEffect(() => {
    if (isMounted) {
      updateLogoTextures(selectedLogoImages, wrapImage);
    }
  }, [selectedLogoImages, wrapImage]);

  useEffect(() => {
    if (selectedSwatches && selectedFocusCoordinates && isMounted) {
      if (selectedSwatches.color) {
        Object.keys(selectedSwatches.color).forEach((side) => {
          const { canWrap, syncWithElement } = model.initialMap.find(
            (map) => map.name === model.sides[side].element
          );

          setColor(
            model.sides[side].element,
            selectedSwatches.color[side],
            canWrap,
            envMap
          );

          // TODO last minute K3 hack requested by Knightscope
          console.log(model.sides[side].element);
          if (model.sides[side].element === "K3-DetailUpdate_7") {
            setColor(
              "K3-DetailUpdate_12",
              selectedSwatches.color[side],
              canWrap,
              envMap
            );
            setColor(
              "K3-DetailUpdate_13",
              selectedSwatches.color[side],
              canWrap,
              envMap
            );
          }

          if (syncWithElement) {
            setColor(
              syncWithElement,
              selectedSwatches.color[side],
              canWrap,
              envMap
            );
          }
        });
      }
    }
  }, [selectedSwatches, selectedFocusCoordinates]);

  useEffect(() => {
    if (summaryViewsTimestamp) {
      generateSummaryViews(availableSides);
    }
  }, [summaryViewsTimestamp]);

  const calculateImageSize = (image, placeholder) => {
    const imageWidth = image.width;
    const imageHeight = image.height;
    const placeholderWidth = placeholder.width;
    const placeholderHeight = placeholder.height;

    const { width, height } = resizeImg(
      imageWidth,
      imageHeight,
      placeholderWidth,
      placeholderHeight
    );

    return {
      width,
      height,
    };
  };

  const updateLogoTextures = async (logos, wrap) => {
    const addImageProcess = (img, ctx) => {
      return new Promise(async (resolve, reject) => {
        const logoImage = new Image();
        const imgBase64 = await toBase64(img.file);

        logoImage.onload = () => {
          const position = img.coords;
          const { width, height } = calculateImageSize(logoImage, position);

          ctx.drawImage(
            logoImage,
            position.x + (position.width - width) / 2,
            position.y + (position.height - height) / 2,
            width,
            height
          );

          resolve(img);
        };
        logoImage.onerror = reject;
        logoImage.src = imgBase64;
      });
    };

    const fillTexture = async (texture) => {
      const ctxLogos = logosPlane.getContext("2d");
      ctxLogos.clearRect(
        0,
        0,
        model.textureSize.width,
        model.textureSize.height
      );

      // draw wrap
      const ctxWrap = wrapPlane.getContext("2d");
      ctxWrap.clearRect(
        0,
        0,
        model.textureSize.width,
        model.textureSize.height
      );

      if (texture) {
        ctxWrap.drawImage(texture, 0, 0, logosPlane.width, logosPlane.height);
      }

      drawUpdatedTexture(wrapPlane, true);

      // draw uploaded images
      await Promise.all(
        Object.values(logos).map(async (img) => {
          if (!img.file) {
            return;
          }

          await addImageProcess(img, ctxLogos);
        })
      );
      drawUpdatedTexture(logosPlane, false);

      // draw stickers
      if (model.textureTransparent) {
        drawStickers(ctxLogos, logosPlane);
      }
    };

    const wrapImage = wrap ? await toBase64(wrap) : null;
    if (wrapImage) {
      const wrapTxtr = new Image();
      wrapTxtr.src = wrapImage;
      wrapTxtr.onload = async () => {
        await fillTexture(wrapTxtr);
      };
    } else {
      await fillTexture(null);
    }
  };

  const drawUpdatedTexture = (plane, isWrap = false) => {
    const txtr = new THREE.CanvasTexture(plane);
    txtr.flipY = false;
    txtr.needsUpdate = true;
    txtr.minFilter = THREE.LinearFilter;
    txtr.anisotropy = renderer.capabilities.getMaxAnisotropy();

    logoTexture = txtr;
    model.initialMap.forEach(({ name, canWrap, canCloneWrap }) => {
      if (isWrap) {
        setLogoTexture(name + "-clone-wrap", canWrap);
      } else {
        setLogoTexture(name + "-clone", canWrap || canCloneWrap);
      }
    });
    onFocus(renderer.domElement.toDataURL(strMime));
  };

  const drawStickers = (ctx, plane) => {
    const strickers = new Image();
    strickers.src = model.textureTransparent;
    strickers.onload = async () => {
      ctx.drawImage(strickers, 0, 0, plane.width, plane.height);
      drawUpdatedTexture(plane, false);
    };
  };

  const addLight = () => {
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.8);
    hemiLight.position.set(100, 600, 100);
    scene.add(hemiLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.45);
    dirLight.castShadow = false;
    dirLight.shadow.camera.near = 0.2;
    dirLight.shadow.camera.far = 2000;
    dirLight.shadow.radius = 1;
    dirLight.shadow.mapSize = new THREE.Vector2(2048, 2048);
    dirLight.position.set(100, 600, 100);
    scene.add(dirLight);
  };

  const addFloor = () => {
    const floorGeometry = new THREE.PlaneGeometry(5000, 5000, 1, 1);
    const floorMaterial = new THREE.ShadowMaterial({
      opacity: 0,
    });

    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -0.5 * Math.PI;
    floor.receiveShadow = false;
    floor.position.y = -1;
    scene.add(floor);
  };

  const addShadow = () => {
    const shadow = textureLoader.load(
      require("../../assets/models/shadow.png")
    );
    shadow.needsUpdate = true;
    const multiplication = model.shadowSize || 1;
    const geometry = new THREE.PlaneGeometry(multiplication, multiplication);
    const material = new THREE.MeshBasicMaterial({
      map: shadow,
      opacity: 0.4,
      side: THREE.DoubleSide,
      transparent: true,
    });
    const sprite = new THREE.Mesh(geometry, material);
    sprite.rotation.x = -0.5 * Math.PI;
    sprite.position.y = -1;
    scene.add(sprite);
  };

  const addControls = () => {
    controls = new OrbitControls(camera, renderer.domElement);
    controls.maxPolarAngle = Math.PI / 2;
    controls.enableDamping = true;
    controls.enablePan = false;
    controls.dampingFactor = 0.1;
    controls.autoRotate = false;
    controls.autoRotateSpeed = 0.2;
    controls.enabled = false;
    controls.target.set(0, model.controlTarget, 0);
  };

  const focusModel = ({ rotation }) => {
    let initial = selectedModel.rotation.y;
    const step = initial < rotation ? Math.PI / 36 : -Math.PI / 36;
    const rotate = () => {
      initial += step;

      if (step < 0) {
        if (initial >= rotation) {
          selectedModel.rotation.y += step;
          return requestAnimationFrame(rotate);
        }
      } else {
        if (initial - Math.PI / 36 <= rotation) {
          selectedModel.rotation.y += step;
          return requestAnimationFrame(rotate);
        }
      }

      onFocus(renderer.domElement.toDataURL(strMime));
    };

    requestAnimationFrame(rotate);
  };

  const generateSummaryViews = async (availableSides) => {
    const values = [];
    const sides = Object.values(availableSides).map((side) => ({
      rotation: side.rotation,
      name: side.name,
    }));
    selectedModel.rotation.y = 0;

    const generate = () => {
      if (sides.length) {
        selectedModel.rotation.y = sides[0].rotation;
        requestAnimationFrame(() => {
          values.push({
            name: sides[0].name,
            value: renderer.domElement.toDataURL(strMime),
          });
          sides.splice(0, 1);
          requestAnimationFrame(generate);
        });
      } else {
        selectedModel.rotation.y = availableSides[BACK_SIDE].rotation;
        onSummaryViewsGenerated([
          values.find((value) => value.name === availableSides[BACK_SIDE].name),
          values.find(
            (value) => value.name === availableSides[FRONT_SIDE].name
          ),
          values.find((value) => value.name === availableSides[LEFT_SIDE].name),
          values.find(
            (value) => value.name === availableSides[RIGHT_SIDE].name
          ),
        ]);
      }
    };

    requestAnimationFrame(generate);
  };

  const setColor = (
    element,
    color,
    override = false,
    envMap,
    isStandardMaterial = false
  ) => {
    const specular = 0x050505;
    const roughness = 0;
    const metalness = 0;
    const opacity = 1;

    selectedModel.traverse((o) => {
      if (o.isMesh && o.name) {
        if (o.name === element) {
          o.castShadow = false;
          o.receiveShadow = false;
          let mtl = o.material;
          const mColor = color ? parseInt("0x" + color) : o.material.color;
          if (override) {
            // TODO k5 exceptions due to badly formatted model
            if (
              o.name.includes("K5Glossy-FinalOBJ_2") ||
              o.name.includes("K5TextureUpdate4_1")
            ) {
              mtl = new THREE.MeshPhongMaterial({
                opacity,
                color: mColor,
                specular,
                roughness,
                map: altTexture,
                envMap,
                metalness,
              });
            } else {
              mtl = new THREE.MeshPhongMaterial({
                opacity,
                color: mColor,
                specular,
                roughness,
                map: texture,
                envMap,
                metalness,
              });
            }
          } else {
            if (isStandardMaterial) {
              mtl = new THREE.MeshStandardMaterial({
                opacity,
                color: mColor,
                specular,
                roughness,
                envMap,
                metalness,
              });
            } else {
              mtl = new THREE.MeshPhongMaterial({
                opacity,
                color: mColor,
                specular,
                roughness,
                envMap,
                metalness,
              });
            }
          }

          o.material = mtl;
        }
      }
    });
  };

  const setLogoTexture = (element, canWrap) => {
    selectedModel.traverse((o) => {
      if (o.isMesh && o.name) {
        // TODO make generic
        if (
          o.name === element &&
          canWrap &&
          !o.name.includes("K5Glossy-FinalOBJ_2") &&
          !o.name.includes("K5TextureUpdate4_1")
        ) {
          o.material = new THREE.MeshStandardMaterial({
            transparent: true,
            map: logoTexture,
          });
        }
      }
    });
  };

  const addCamera = (selectedFocusCoordinates) => {
    const canvas = renderer.domElement;
    const refWidth = canvas.parentElement.clientWidth;
    const refHeight = canvas.parentElement.clientHeight;
    camera = new THREE.PerspectiveCamera(70, refWidth / refHeight, 1, 1000);
    camera.position.z = selectedFocusCoordinates.camera;
    camera.zoom = 1.6;
  };

  const resize = (renderer, camera) => {
    if (!renderer || !camera) {
      return;
    }

    const canvas = renderer.domElement;
    const refWidth = canvas.parentElement.clientWidth;
    const refHeight = canvas.parentElement.clientHeight;

    camera.aspect = refWidth / refHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(refWidth, refHeight, false);
  };

  const loadModel = (path) => {
    const loader = new GLTFLoader();
    const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
    loader.load(
      path,
      (gltf) => {
        selectedModel = gltf.scene;
        envMap = textureLoader.load(model.envMap);
        envMap.mapping = THREE.EquirectangularReflectionMapping;
        texture = textureLoader.load(model.texture);
        texture.anisotropy = maxAnisotropy;
        texture.flipY = false;
        texture.needsUpdate = true;

        // alt texture
        if (model.altTexture) {
          altTexture = textureLoader.load(model.altTexture);
          altTexture.anisotropy = maxAnisotropy;
          altTexture.flipY = false;
          altTexture.needsUpdate = true;
        }

        //prep wrap plane
        wrapPlane = document.createElement("canvas");
        wrapPlane.width = model.textureSize.width;
        wrapPlane.height = model.textureSize.height;

        //prep logos plane
        logosPlane = document.createElement("canvas");
        logosPlane.width = model.textureSize.width;
        logosPlane.height = model.textureSize.height;

        // branding stickers texture
        if (model.textureTransparent) {
          textureTransparent = textureLoader.load(model.textureTransparent);
          textureTransparent.anisotropy = maxAnisotropy;
          textureTransparent.flipY = false;
          textureTransparent.needsUpdate = true;

          const ctxLogos = logosPlane.getContext("2d");
          drawStickers(ctxLogos, logosPlane);
        }

        selectedModel.scale.set(model.scale, model.scale, model.scale);
        selectedModel.rotation.y = model.sides[BACK_SIDE].rotation;
        selectedModel.position.y = -1;

        // initial materials
        model.initialMap.forEach(
          ({ name, color, canWrap, isStandardMaterial }) => {
            setColor(name, color, canWrap, envMap, isStandardMaterial);
          }
        );

        // update materials
        selectedModel.traverse((o) => {
          if (o.userData && o.userData.name === model.groupName) {
            modelGroup = o;
          }

          if (o.isMesh) {
            o.castShadow = true;
            o.receiveShadow = true;

            if (o.geometry) {
              // smooth geometries
              // const tempGeometry = BufferGeometryUtils.mergeVertices(
              //   o.geometry
              // );
              // tempGeometry.computeVertexNormals();
              // tempGeometry.computeFaceNormals();
              // o.geometry = tempGeometry;

              // build logo layers
              const clonedMesh = o.clone();
              clonedMesh.name = o.name + "-clone";
              clonedMesh.receiveShadow = false;
              clonedMesh.castShadow = false;
              clonedMesh.material = new THREE.MeshStandardMaterial({
                transparent: true,
                needsUpdate: true,
                opacity: 0,
              });
              clonedMesh.renderOrder = 5;
              meshLogoClones.push(clonedMesh);

              // build wrap layers
              const clonedWrapMesh = o.clone();
              clonedWrapMesh.name = o.name + "-clone-wrap";
              clonedWrapMesh.receiveShadow = false;
              clonedWrapMesh.castShadow = false;
              clonedWrapMesh.material = new THREE.MeshStandardMaterial({
                transparent: true,
                needsUpdate: true,
                opacity: 0,
              });
              clonedWrapMesh.renderOrder = 3;

              meshWrapClones.push(clonedWrapMesh);
            }
          }
        });

        // append wrap layers
        if (modelGroup && meshWrapClones.length) {
          meshWrapClones.forEach((clone) => {
            modelGroup.add(clone);
          });
        }

        // append logo layers
        if (modelGroup && meshLogoClones.length) {
          meshLogoClones.forEach((clone) => {
            modelGroup.add(clone);
          });
        }

        scene.add(selectedModel);
        setTimeout(() => {
          setLoading(false);
          addShadow();
          onModelLoaded(selectedModel);
          if (renderer.domElement) {
            onFocus(renderer.domElement.toDataURL(strMime));
          }
        }, 2000);
      },
      undefined,
      (error) => {
        console.error(error);
      }
    );
  };

  useEffect(() => {
    const canvas = container.current;
    const refWidth = canvas.parentElement.clientWidth;
    const refHeight = canvas.parentElement.clientHeight;
    scene = new THREE.Scene();

    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(refWidth, refHeight);
    renderer.shadowMap.enabled = true;
    renderer.gammaOutput = false;
    renderer.setPixelRatio(window.devicePixelRatio);

    addCamera(selectedFocusCoordinates);
    addLight();
    addControls();
    loadModel(model.mesh);

    const animate = () => {
      if (!renderer || !camera || !controls) {
        return;
      }

      resize(renderer, camera);

      controls.update();
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
    isMounted = true;

    return () => {
      const ctxLogos = logosPlane.getContext("2d");
      ctxLogos.clearRect(
        0,
        0,
        model.textureSize.width,
        model.textureSize.height
      );

      const ctxWrap = wrapPlane.getContext("2d");
      ctxWrap.clearRect(
        0,
        0,
        model.textureSize.width,
        model.textureSize.height
      );

      scene = null;
      camera = null;
      renderer = null;
      controls = null;
      texture = null;
      altTexture = null;
      textureTransparent = null;
      logoTexture = null;
      logosPlane = null;
      wrapPlane = null;
      envMap = null;
      modelGroup = null;
      isMounted = false;
      meshLogoClones = [];
      meshWrapClones = [];
    };
  }, []);

  return (
    <>
      <canvas key={model.name} ref={container}></canvas>
      {loading ? <Loader /> : <Watermark />}
    </>
  );
};

export default SceneComponent;
